import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiArrowLeft, FiEdit, FiDownload, FiMail, FiCreditCard, FiCheck, 
  FiX, FiClock, FiUser, FiCalendar, FiFileText, FiPackage, FiPercent,
  FiTrash2, FiCopy, FiShare2, FiEye, FiEyeOff, FiSend
} from 'react-icons/fi';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'react-toastify';
import { Invoice } from '../../types';
import { downloadInvoicePDF } from '../../utils/pdfGenerator';
import styles from './InvoiceView.module.css';

// Mock invoice data for demonstration
const mockInvoice: Invoice = {
  id: '1',
  invoiceNumber: 'INV-202401-001',
  clientId: '1',
  client: {
    id: '1',
    name: 'TechCorp Solutions',
    email: 'contact@techcorp.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Business Ave',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94105',
      country: 'USA'
    },
    taxNumber: 'TAX123456',
    vatNumber: 'VAT789012',
    paymentTerms: 'net30',
    notes: 'Preferred contact method: email',
    tags: ['VIP', 'Tech'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-15')
  },
  items: [
    {
      id: '1',
      productId: '1',
      description: 'Web Development Services - Custom e-commerce platform with modern UI/UX design',
      quantity: 1,
      unitPrice: 5000,
      taxRate: 10,
      taxAmount: 500,
      total: 5000
    },
    {
      id: '2',
      productId: '2',
      description: 'UI/UX Design - Complete design system and user experience optimization',
      quantity: 1,
      unitPrice: 2500,
      taxRate: 10,
      taxAmount: 250,
      total: 2500
    },
    {
      id: '3',
      productId: '3',
      description: 'Consulting Services - Technical architecture and project planning',
      quantity: 20,
      unitPrice: 150,
      taxRate: 10,
      taxAmount: 300,
      total: 3000
    }
  ],
  subtotal: 10500,
  taxAmount: 1050,
  discountAmount: 500,
  total: 11050,
  status: 'sent',
  issueDate: new Date('2024-01-15'),
  dueDate: new Date('2024-02-15'),
  notes: 'Payment is due within 30 days. Please include invoice number with payment.',
  terms: 'Net 30 days. Late payments may incur additional fees.',
  createdBy: 'John Doe',
  createdAt: new Date('2024-01-15'),
  updatedAt: new Date('2024-01-15')
};

export function InvoiceView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice] = useState<Invoice>(mockInvoice);
  const [showActions, setShowActions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isOverdue = isAfter(new Date(), invoice.dueDate) && invoice.status !== 'paid';
  const daysUntilDue = Math.ceil((invoice.dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'var(--gray-600)', bg: 'var(--gray-100)', icon: <FiFileText size={16} /> },
      sent: { color: 'var(--blue-600)', bg: 'var(--blue-100)', icon: <FiMail size={16} /> },
      paid: { color: 'var(--green-600)', bg: 'var(--green-100)', icon: <FiCheck size={16} /> },
      overdue: { color: 'var(--red-600)', bg: 'var(--red-100)', icon: <FiClock size={16} /> },
      cancelled: { color: 'var(--orange-600)', bg: 'var(--orange-100)', icon: <FiX size={16} /> }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <span className={styles.statusBadge} style={{ color: config.color, backgroundColor: config.bg }}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleMarkAsPaid = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Invoice marked as paid successfully!');
      // In a real app, this would update the invoice status
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Invoice sent successfully!');
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsLoading(true);
      
      // Prepare data for PDF generation
      const pdfData = {
        invoiceNumber: invoice.invoiceNumber,
        issueDate: invoice.issueDate.toISOString().split('T')[0],
        dueDate: invoice.dueDate.toISOString().split('T')[0],
        client: {
          name: invoice.client.name,
          email: invoice.client.email,
          phone: invoice.client.phone || '',
          company: invoice.client.name, // Using client name as company for now
          address: invoice.client.address ? 
            `${invoice.client.address.street}, ${invoice.client.address.city}, ${invoice.client.address.state} ${invoice.client.address.zipCode}` : 
            undefined
        },
        items: invoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.total
        })),
        subtotal: invoice.subtotal,
        tax: invoice.taxAmount,
        total: invoice.total,
        notes: invoice.notes,
        terms: invoice.terms
      };

      await downloadInvoicePDF(pdfData);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleDeleteInvoice = () => {
    if (window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      toast.success('Invoice deleted successfully!');
      navigate('/invoices');
    }
  };

  return (
    <motion.div 
      className={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/invoices" className={styles.backButton}>
            <FiArrowLeft size={16} />
            Back to Invoices
          </Link>
          <div className={styles.invoiceInfo}>
            <h1 className={styles.title}>{invoice.invoiceNumber}</h1>
            <p className={styles.subtitle}>
              Created for {invoice.client.name} • {format(invoice.issueDate, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>
        
        <div className={styles.headerActions}>
          <div className={styles.statusSection}>
            {getStatusBadge(invoice.status)}
            {invoice.status === 'sent' && !isOverdue && (
              <span className={styles.dueInfo}>
                Due in {daysUntilDue} days
              </span>
            )}
            {isOverdue && (
              <span className={styles.overdueInfo}>
                Overdue by {Math.abs(daysUntilDue)} days
              </span>
            )}
          </div>
          
          <div className={styles.actionButtons}>
            <button 
              onClick={() => setShowActions(!showActions)}
              className={styles.moreButton}
            >
              {showActions ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              Actions
            </button>
            
            <AnimatePresence>
              {showActions && (
                <motion.div 
                  className={styles.actionsDropdown}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link to={`/invoices/${id}/edit`} className={styles.actionItem}>
                    <FiEdit size={16} />
                    Edit Invoice
                  </Link>
                  <button 
                    onClick={handleDownloadPDF} 
                    className={styles.actionItem}
                    disabled={isLoading}
                  >
                    <FiDownload size={16} />
                    {isLoading ? 'Generating...' : 'Download PDF'}
                  </button>
                  <button onClick={handleSendInvoice} className={styles.actionItem}>
                    <FiMail size={16} />
                    Send Invoice
                  </button>
                  <button onClick={handleCopyLink} className={styles.actionItem}>
                    <FiCopy size={16} />
                    Copy Link
                  </button>
                  <button onClick={handleDeleteInvoice} className={`${styles.actionItem} ${styles.deleteAction}`}>
                    <FiTrash2 size={16} />
                    Delete Invoice
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Main Content */}
        <div className={styles.mainSection}>
          <div className={styles.invoiceCard}>
            {/* Invoice Header */}
            <div className={styles.invoiceHeader}>
              <div className={styles.companyInfo}>
                <h2>C-Band</h2>
                <p>Professional Invoicing & Accounting</p>
                <p>123 Business Street</p>
                <p>City, State 12345</p>
                <p>info@c-band.com</p>
              </div>
              <div className={styles.invoiceDetails}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Invoice Number:</span>
                  <span className={styles.detailValue}>{invoice.invoiceNumber}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Issue Date:</span>
                  <span className={styles.detailValue}>{format(invoice.issueDate, 'MMM dd, yyyy')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Due Date:</span>
                  <span className={styles.detailValue}>{format(invoice.dueDate, 'MMM dd, yyyy')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status:</span>
                  <span className={styles.detailValue}>{getStatusBadge(invoice.status)}</span>
                </div>
              </div>
            </div>

            {/* Client Section */}
            <div className={styles.clientSection}>
              <h3>Bill To:</h3>
              <div className={styles.clientInfo}>
                <h4>{invoice.client.name}</h4>
                <p>{invoice.client.email}</p>
                {invoice.client.phone && <p>{invoice.client.phone}</p>}
                {invoice.client.address && (
                  <div className={styles.address}>
                    <p>{invoice.client.address.street}</p>
                    <p>{invoice.client.address.city}, {invoice.client.address.state} {invoice.client.address.zipCode}</p>
                    <p>{invoice.client.address.country}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Items Section */}
            <div className={styles.itemsSection}>
              <table className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Tax</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td className={styles.itemDescription}>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>${item.unitPrice.toFixed(2)}</td>
                      <td>${item.taxAmount.toFixed(2)}</td>
                      <td>${item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals Section */}
            <div className={styles.totalsSection}>
              <div className={styles.totalRow}>
                <span>Subtotal:</span>
                <span>${invoice.subtotal.toFixed(2)}</span>
              </div>
              {invoice.discountAmount > 0 && (
                <div className={styles.totalRow}>
                  <span>Discount:</span>
                  <span>-${invoice.discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className={styles.totalRow}>
                <span>Tax:</span>
                <span>${invoice.taxAmount.toFixed(2)}</span>
              </div>
              <div className={`${styles.totalRow} ${styles.grandTotal}`}>
                <span>Total:</span>
                <span>${invoice.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Notes Section */}
            {invoice.notes && (
              <div className={styles.notesSection}>
                <h3>Notes:</h3>
                <p>{invoice.notes}</p>
              </div>
            )}

            {/* Terms Section */}
            {invoice.terms && (
              <div className={styles.termsSection}>
                <h3>Terms & Conditions:</h3>
                <p>{invoice.terms}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>
          {/* Quick Actions */}
          <div className={styles.sidebarCard}>
            <h3>Quick Actions</h3>
            <div className={styles.quickActions}>
              {invoice.status === 'sent' && (
                <button 
                  onClick={handleMarkAsPaid}
                  disabled={isLoading}
                  className={`${styles.actionButton} ${styles.paidButton}`}
                >
                  <FiCheck size={16} />
                  Mark as Paid
                </button>
              )}
              
              {invoice.status === 'draft' && (
                <button 
                  onClick={handleSendInvoice}
                  disabled={isLoading}
                  className={`${styles.actionButton} ${styles.sendButton}`}
                >
                  <FiSend size={16} />
                  Send Invoice
                </button>
              )}
            </div>
          </div>

          {/* Invoice Summary */}
          <div className={styles.sidebarCard}>
            <h3>Invoice Summary</h3>
            <div className={styles.summaryItem}>
              <span>Status:</span>
              <span>{getStatusBadge(invoice.status)}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Created:</span>
              <span>{format(invoice.createdAt, 'MMM dd, yyyy')}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Created By:</span>
              <span>{invoice.createdBy}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Items:</span>
              <span>{invoice.items.length}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Total Amount:</span>
              <span className={styles.totalAmount}>${invoice.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Client Information */}
          <div className={styles.sidebarCard}>
            <h3>Client Information</h3>
            <div className={styles.clientSummary}>
              <h4>{invoice.client.name}</h4>
              <p>{invoice.client.email}</p>
              {invoice.client.phone && <p>{invoice.client.phone}</p>}
              <Link to={`/clients/${invoice.client.id}`} className={styles.viewClientButton}>
                <FiUser size={16} />
                View Client
              </Link>
            </div>
          </div>

          {/* Timeline */}
          <div className={styles.sidebarCard}>
            <h3>Timeline</h3>
            <div className={styles.timeline}>
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Invoice Created</strong>
                  <p>{format(invoice.createdAt, 'MMM dd, yyyy')}</p>
                </div>
              </div>
              
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Invoice Sent</strong>
                  <p>{format(invoice.issueDate, 'MMM dd, yyyy')}</p>
                </div>
              </div>
              
              {invoice.status === 'paid' && invoice.paidDate && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineDot} />
                  <div className={styles.timelineContent}>
                    <strong>Payment Received</strong>
                    <p>{format(invoice.paidDate, 'MMM dd, yyyy')}</p>
                  </div>
                </div>
              )}
              
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Due Date</strong>
                  <p>{format(invoice.dueDate, 'MMM dd, yyyy')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
